import { useState, useEffect, useCallback, useRef } from 'react';

/* 
  This Regex accepts both EthioTelecom & Safaricom numbers 
  starting with +2519 | +2517 | 2519 | 2517 | 09 | 07 | 9 | 7 
*/
const phoneNormalizationRegex = /^(?:\+?251([79]))?(0?)([79])(\d{8})$/;

const normalizePhone = (phone) => {
  if (!phone) return '';

  // Ensure phone is a string
  const phoneStr = String(phone);

  // Remove all non-digit characters and + sign for processing
  const cleanPhone = phoneStr.replace(/[^\d+]/g, '');
  const match = cleanPhone.match(phoneNormalizationRegex);

  if (match) {
    const [, countryAreaCode, leadingZero, areaCode, number] = match;

    // If we have country code area (7 or 9 from +251[79])
    if (countryAreaCode) {
      return `251${countryAreaCode}${number}`;
    }

    // If we have leading zero (07 or 09)
    if (leadingZero === '0') {
      return `251${areaCode}${number}`;
    }

    // Single digit area code (7 or 9)
    return `251${areaCode}${number}`;
  }

  // Return original if no pattern matches (fallback)
  return phoneStr.replace(/\D/g, '');
};

// Simple hash function for data integrity check
const simpleHash = (str) => {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// Create a signature for data integrity
const createSignature = (data) => {
  const dataStr = JSON.stringify(data);
  return simpleHash(dataStr + 'otp_security_salt_2024');
};

// Verify data integrity
const verifySignature = (data, signature) => {
  return createSignature(data) === signature;
};

/**
 * Custom hook for OTP resend cooldown with progressive delays
 * @param {string} flowKey - Unique identifier for the flow (e.g., 'changePhone', 'register', 'forgetPassword', 'requestOtp')
 * @param {string} phoneNumber - Phone number to associate with the cooldown (optional)
 * @param {Object} options - Configuration options
 * @param {number} options.initialDelay - Initial cooldown delay in seconds (default: 60)
 * @param {number} options.maxDelay - Maximum cooldown delay in seconds (default: 300)
 * @param {number} options.multiplier - Delay multiplier for each subsequent request (default: 1.5)
 * @param {number} options.maxAttempts - Maximum attempts before blocking (default: 5)
 * @param {number} options.resetAfterHours - Reset counter after this many hours (default: 6)
 * @param {number} options.blockDurationHours - Block duration after max attempts (default: 1)
 */
export const useOtpResendCooldown = (flowKey, phoneNumber = '', options = {}) => {
  const {
    initialDelay = 60,
    maxDelay = 300,
    multiplier = 1.5,
    maxAttempts = 5,
    resetAfterHours = 6,
    blockDurationHours = 1
  } = options;

  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockEndTime, setBlockEndTime] = useState(0);
  const intervalRef = useRef(null);

  // Create phone-specific storage key
  const normalizedPhone = normalizePhone(phoneNumber);
  const storageKey = normalizedPhone
    ? `otp_cooldown_${flowKey}_${normalizedPhone}`
    : `otp_cooldown_${flowKey}`;

  // Load cooldown state from localStorage with security checks
  const loadCooldownState = useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const parsedData = JSON.parse(stored);
      const { signature, ...data } = parsedData;

      // Verify data integrity
      if (!signature || !verifySignature(data, signature)) {
        console.warn('OTP data integrity check failed - clearing corrupted data');
        localStorage.removeItem(storageKey);
        return null;
      }

      const now = Date.now();

      // Check if data should be reset after the specified hours
      const resetTime = resetAfterHours * 60 * 60 * 1000;
      if (now - data.firstAttemptTime > resetTime) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return data;
    } catch (error) {
      console.warn('Failed to load OTP cooldown state:', error);
      localStorage.removeItem(storageKey);
      return null;
    }
  }, [storageKey, resetAfterHours]);

  // Save cooldown state to localStorage with security signature
  const saveCooldownState = useCallback((data) => {
    try {
      const signature = createSignature(data);
      const secureData = { ...data, signature };
      localStorage.setItem(storageKey, JSON.stringify(secureData));
    } catch (error) {
      console.warn('Failed to save OTP cooldown state:', error);
    }
  }, [storageKey]);

  // Calculate current delay based on attempt count
  const getCurrentDelay = useCallback((attempts) => {
    if (attempts === 0) return initialDelay;

    let delay = initialDelay;
    for (let i = 1; i < Math.min(attempts, maxAttempts); i++) {
      delay *= multiplier;
    }

    return Math.min(delay, maxDelay);
  }, [initialDelay, maxDelay, multiplier, maxAttempts]);

  // Start cooldown timer with attempt limits
  const startCooldown = useCallback(() => {
    const now = Date.now();
    const stored = loadCooldownState();

    let newAttemptCount;
    let firstAttemptTime;
    let blockUntilTime = null;

    if (stored) {
      newAttemptCount = stored.attemptCount + 1;
      firstAttemptTime = stored.firstAttemptTime;
      blockUntilTime = stored.blockUntilTime;
    } else {
      newAttemptCount = 1;
      firstAttemptTime = now;
    }

    // Check if user has exceeded max attempts and should be blocked
    if (newAttemptCount > maxAttempts) {
      const blockDuration = blockDurationHours * 60 * 60 * 1000;
      blockUntilTime = now + blockDuration;

      const newState = {
        attemptCount: newAttemptCount,
        cooldownEndTime: 0,
        firstAttemptTime,
        blockUntilTime
      };
      saveCooldownState(newState);

      setAttemptCount(newAttemptCount);
      setIsBlocked(true);
      setBlockEndTime(blockUntilTime);
      setIsOnCooldown(false);
      setRemainingTime(0);
      return;
    }

    const delay = getCurrentDelay(newAttemptCount);
    const cooldownEndTime = now + (delay * 1000);

    // Save updated state
    const newState = {
      attemptCount: newAttemptCount,
      cooldownEndTime,
      firstAttemptTime,
      blockUntilTime
    };
    saveCooldownState(newState);

    setAttemptCount(newAttemptCount);
    setIsOnCooldown(true);
    setRemainingTime(delay);
    setIsBlocked(false);

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start countdown
    intervalRef.current = setInterval(() => {
      const timeLeft = Math.max(0, Math.ceil((cooldownEndTime - Date.now()) / 1000));
      setRemainingTime(timeLeft);

      if (timeLeft === 0) {
        setIsOnCooldown(false);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);
  }, [loadCooldownState, saveCooldownState, getCurrentDelay, maxAttempts, blockDurationHours]);

  // Reset cooldown state
  const resetCooldown = useCallback(() => {
    localStorage.removeItem(storageKey);
    setIsOnCooldown(false);
    setRemainingTime(0);
    setAttemptCount(0);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [storageKey]);

  // Check if should resume existing cooldown on component mount
  useEffect(() => {
    const stored = loadCooldownState();
    if (stored) {
      const now = Date.now();
      setAttemptCount(stored.attemptCount);

      // Check if user is blocked
      if (stored.blockUntilTime && now < stored.blockUntilTime) {
        setIsBlocked(true);
        setBlockEndTime(stored.blockUntilTime);
        setIsOnCooldown(false);

        // Show remaining block time and prevent retry until block ends
        const blockInterval = setInterval(() => {
          const timeLeft = Math.max(0, Math.ceil((stored.blockUntilTime - Date.now()) / 1000));
          setBlockEndTime(stored.blockUntilTime);

          if (timeLeft === 0) {
            setIsBlocked(false);
            setAttemptCount(0); // Reset attempts after block ends
            // Remove block state from localStorage
            localStorage.removeItem(storageKey);
            clearInterval(blockInterval);
          }
        }, 1000);

        return () => {
          clearInterval(blockInterval);
        };
      }

      // Check regular cooldown
      const timeLeft = Math.max(0, Math.ceil((stored.cooldownEndTime - now) / 1000));
      if (timeLeft > 0) {
        setIsOnCooldown(true);
        setRemainingTime(timeLeft);
        setIsBlocked(false);

        // Resume countdown
        intervalRef.current = setInterval(() => {
          const currentTimeLeft = Math.max(0, Math.ceil((stored.cooldownEndTime - Date.now()) / 1000));
          setRemainingTime(currentTimeLeft);

          if (currentTimeLeft === 0) {
            setIsOnCooldown(false);
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }, 1000);
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadCooldownState, storageKey]);

  // Format remaining time for display
  const formatRemainingTime = useCallback((seconds) => {
    if (seconds <= 0) return '';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${remainingSeconds}s`;
  }, []);

  // Format block time for display
  const formatBlockTime = useCallback(() => {
    if (!isBlocked || blockEndTime === 0) return '';

    const now = Date.now();
    const timeLeft = Math.max(0, Math.ceil((blockEndTime - now) / 1000));

    if (timeLeft === 0) {
      setIsBlocked(false);
      setAttemptCount(0); // Reset attempts after block ends
      localStorage.removeItem(storageKey);
      return '';
    }

    const hours = Math.floor(timeLeft / 3600);
    const minutes = Math.floor((timeLeft % 3600) / 60);
    const seconds = timeLeft % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, [isBlocked, blockEndTime, storageKey]);

  // Get message based on current state
  const getMessage = useCallback(() => {
    if (isBlocked) {
      return {
        type: 'blocked',
        title: '🚫 Too Many Attempts',
        message: `You've exceeded the maximum number of OTP requests. Please try again after ${formatBlockTime()}.`,
        action: 'blocked'
      };
    }

    if (isOnCooldown) {
      const remaining = attemptCount < maxAttempts ? maxAttempts - attemptCount : 0;
      return {
        type: 'cooldown',
        title: `⏱️ Please Wait ${formatRemainingTime(remainingTime)} to Resend OTP`,
        message: remaining > 0
          ? `You can make ${remaining} more attempt${remaining > 1 ? 's' : ''} before being temporarily blocked.`
          : `Next attempt will result in a temporary block.`,
        action: 'wait'
      };
    }

    if (attemptCount === 0) {
      return {
        type: 'ready',
        title: '📱 Resend OTP',
        message: 'Didn\'t receive the code? We can send a new one.',
        action: 'send'
      };
    }

    const remaining = maxAttempts - attemptCount;
    return {
      type: 'ready',
      title: '📱 Resend OTP',
      message: remaining > 0
        ? `${remaining} attempt${remaining > 1 ? 's' : ''} remaining before temporary block.`
        : 'This is your last attempt before being temporarily blocked.',
      action: 'send'
    };
  }, [isBlocked, isOnCooldown, attemptCount, maxAttempts, remainingTime, formatRemainingTime, formatBlockTime]);

  return {
    // State
    isOnCooldown,
    isBlocked,
    remainingTime,
    attemptCount,

    // Computed properties
    canResend: !isOnCooldown && !isBlocked,
    formattedTime: formatRemainingTime(remainingTime),
    blockTimeRemaining: formatBlockTime(),
    nextDelay: getCurrentDelay(attemptCount + 1),

    // UI helpers
    message: getMessage(),

    // Actions
    startCooldown,
    resetCooldown
  };
};

export default useOtpResendCooldown;
